import { useEffect, useCallback, useState } from 'react';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import Typography from 'apollo-react/components/Typography';
import Box from 'apollo-react/components/Box';
import Button from 'apollo-react/components/Button';
import Attachment from "apollo-react-icons/Paperclip";
import { rolesValidated, loadTreeData } from './constants';
import CommentsList from '../FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from '../CommentToggle/CommentToggle';
import axiosInstance from '../../../axiosInstance';
import { CATEGORIES } from '../../../constants/categories';
import TrashIcon from 'apollo-react-icons/Trash';
import Download from 'apollo-react-icons/Download';
import { useFileDownload } from '../../../hooks';
import { formatFileSize } from '../../../utils/formatFileSize';
import { EctdMetadataView } from './EctdIFrameAttrs';

const Ectd = ({ treeData: propsTreeData = [], role = '', ectdFileId,
    requestProgressStatus = '', comments = [], setComments = () => { }, requestProgressId, requestId, uploadedFile = null,
    showDeleteButton = false, handleDeleteFile = () => { }, ectdVersion = null, isSendbackMode = false, hideMetadata = false, skipCommentsFetch = false }) => {
    
    // State for tree data
    const [treeData, setTreeData] = useState(propsTreeData);
    const [loadingTreeData, setLoadingTreeData] = useState(false);
    
    // Load tree data automatically on mount
    useEffect(() => {
        const fetchTreeData = async () => {
            // If we have tree data from props, use it
            if (propsTreeData && propsTreeData.length > 0) {
                setTreeData(propsTreeData);
                return;
            }
            
            // Otherwise, load from index.json
            setLoadingTreeData(true);
            try {
                const data = await loadTreeData();
                setTreeData(data);
            } catch (error) {
                console.error('Error loading tree data:', error);
            } finally {
                setLoadingTreeData(false);
            }
        };
        
        if(!treeData || treeData.length === 0) {
            fetchTreeData();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchComments = useCallback(async () => {
        if (!requestId) return;
        let allComments = [];
        
        // Fetch comments across ALL ECTD versions using request_id
        const getCommentsLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "request/enduser/" : "request/";
        try {
            const response = await axiosInstance.get(`/api/ectd-comments/${getCommentsLink}${requestId}`,{
                params: {
                    canSeeCommitteeContent: false, // committee ectd members comments will never be shown
                },
            });
            allComments = response.data || [];
        } catch (error) {
            console.error('Error fetching ECTD comments:', error);
            // Fallback to old endpoint if new one doesn't exist or fails
            if (ectdFileId) {
                const fallbackLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser/" : "";
                try {
                    const fallbackResponse = await axiosInstance.get(`/api/ectd-comments/${fallbackLink}${ectdFileId}`,{
                        params: {
                            canSeeCommitteeContent: false, // committee ectd members comments will never be shown
                        }
                    });
                    allComments = fallbackResponse.data || [];
                } catch (fallbackError) {
                    console.error('Fallback fetch also failed:', fallbackError);
                }
            }
        }
        
        // if (requestId && role === ROLES.committee_ectd) {
        //     try {
        //         const responseCoreComments = await axiosInstance.get(`/api/core-validator-directed-committee-comments/core-comments-for-committee/${requestId}`);
        //         allComments = allComments.concat(responseCoreComments.data.map(c => {
        //             return {
        //                 ...c,
        //                 additional_file: c.uploaded_file || null,
        //                 additional_file_id: c.uploaded_file_id || null,
        //                 createdAt: c.createdAt,
        //                 updatedAt: c.updatedAt,
        //                 user: c.User || null,
        //                 comment: c.comment,
        //                 id: c.id,
        //             }
        //         }) || []);
        //     } catch (coreCommentsError) {
        //         console.error('Error fetching core comments:', coreCommentsError);
        //     }
        // }
        
        setComments(allComments);
    }, [requestId, ectdFileId, role]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (requestId && !skipCommentsFetch) {
            fetchComments();
        }
    }, [requestId, fetchComments, skipCommentsFetch]);

    const handleAddComment = async (comment, subFormId, fileId = null, fileData = null) => {
        try {
            const addCommentLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
            const requestData = {
                ectdFileId,
                comment,
            };

            // Add file ID if provided
            if (fileId) {
                requestData.fileId = fileId;
            }

            const response = await axiosInstance.post(`/api/ectd-comments/${addCommentLink}`, requestData);

            // Enrich the response with file data if available
            // set isOwner to true since the current user just created this comment
            const enrichedComment = { ...response.data, isOwner: true };
            if (fileData && fileId) {
                // Convert fileData to the expected additional_file format
                enrichedComment.additional_file = {
                    id: fileData.id,
                    original_name: fileData.original_name,
                    file_path: null, // Not provided by upload response
                    file_size: fileData.file_size,
                    mime_type: fileData.mime_type,
                    createdAt: fileData.created_at,
                    updatedAt: fileData.created_at,
                    deletedAt: null,
                    user_id: null, // Will be set by backend
                    end_user_id: null, // Will be set by backend
                    ...fileData // Include any additional fields
                };
            }

            console.log("Comment added with file data:", enrichedComment);
            setComments(prev => [enrichedComment, ...prev]);
        } catch (error) {
            console.error("Error adding comment:", error);
            throw error;
        }
    };

    // handle edit comment
    const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
        try {
            const editLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
            const requestData = { comment: newText };
            
            if (attachmentInfo.remove_attachment) {
                requestData.remove_attachment = true;
            } else if (attachmentInfo.new_file_id) {
                requestData.additional_file_id = attachmentInfo.new_file_id;
            }

            await axiosInstance.put(`/api/ectd-comments/${editLink ? editLink + '/' : ''}${commentId}`, requestData);
            fetchComments();
        } catch (error) {
            console.error('error editing comment:', error);
            throw error;
        }
    };

    // handle delete comment
    const handleDeleteComment = async (commentId) => {
        try {
            const deleteLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
            await axiosInstance.delete(`/api/ectd-comments/${deleteLink ? deleteLink + '/' : ''}${commentId}`);
            setComments(prev => prev.filter(c => c.id !== commentId));
        } catch (error) {
            console.error('error deleting comment:', error);
            throw error;
        }
    };

    const { downloadFile, downloading } = useFileDownload(role);
    const handleDownloadFile = async () => {
        await downloadFile(uploadedFile.id, uploadedFile.original_name);
    };

    // Recursive render function
    const renderTree = (nodes) => (
        nodes.map((node) => (
            <TreeItem
                key={node.nodeId}
                nodeId={node.nodeId}
                label={node.label}
                icon={node.icon}
            >
                {node.children ? renderTree(node.children) : null}
            </TreeItem>
        ))
    );

    return (
        <div>
            <Box sx={{ marginBottom: 4 }}>
                {!isSendbackMode && !hideMetadata && ectdVersion && (ectdVersion.sequence_number || ectdVersion.version_number) && (
                    <Box sx={{ 
                        marginBottom: 2, 
                    }}>
                        <EctdMetadataView 
                            ectdVersion={ectdVersion} 
                        />
                    </Box>
                )}
                {/* Show loading state while fetching tree data */}
                {loadingTreeData && (
                    <Box sx={{ padding: 2, textAlign: 'center' }}>
                        <Typography variant="bodyDefault">Loading eCTD structure...</Typography>
                    </Box>
                )}
                
                <Box sx={{
                    // maxWidth: 500,
                    borderBottom: "1px solid #e0e0e0",
                    // marginBottom: 3,
                    paddingBottom: 1,

                }}>
                    {treeData && treeData.length > 0 && (
                        <TreeView
                            variant="folder"
                            defaultExpanded={['1']}
                            defaultSelected={['1']}
                            onNodeSelect={(event, value) => {
                                // File opening disabled - clicking on files does nothing
                                return;
                            }}
                        >
                            {renderTree(treeData)}
                        </TreeView>
                    )}
                </Box>

                {uploadedFile && <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginTop: 2,
                  borderRadius: 1,
                }}>
                <Box sx={{ display: "flex", alignItems: "center", height: "29px" }}>
                  <Attachment color="#666" style={{ width: "18px", height: "18px" }} />
                </Box>

                    <Typography
                        variant="bodySmall"
                        // component="h2"
                        sx={{
                            color: "#555",
                            flexGrow: 1,
                            fontWeight: 500,
                            marginRight: 1,
                          }}
                    >
                        {uploadedFile.original_name} {uploadedFile?.file_size && `(${formatFileSize(uploadedFile.file_size)})`}
                    </Typography>

                    <Box sx={{display: "flex", gap: 0.25, alignItems: "center"}}>
                        {showDeleteButton &&
                        <Button
                        variant="secondary"
                        size="small"
                        endIcon={<TrashIcon size={11} />}
                        onClick={handleDeleteFile}
                        sx={{
                            minWidth: "auto",
                            minHeight: "20px",
                            width: "150px",
                            height: "29px",
                            fontSize: "14px",
                            padding: "4px 22.5px",
                            gap: "4px",
                            '& .MuiButton-endIcon': {
                              marginLeft: "2px !important",
                            },
                          }}
                        >
                        <span style={{ fontWeight: 400 }}>Delete</span>
                        </Button>}

                        {
                        <Button
                        variant="secondary"
                        size="small"
                        endIcon={<Download size={11} />}
                        onClick={handleDownloadFile}
                        disabled={downloading}
                        sx={{
                            minWidth: "auto",
                            width: "150px",
                            height: "29px",
                            fontSize: "14px",
                            marginLeft: "8px",
                            padding: "4px 22.5px",
                            backgroundColor: "transparent !important",
                            gap: "4px",
                            '& .MuiButton-endIcon': {
                              marginLeft: "2px !important",
                            }
                          }}
                        >
                        <span style={{ fontWeight: 400 }}>
                            {downloading ? "Downloading..." : "Download"}
                        </span>
                        </Button>
                        }
                    </Box>
                </Box>}
            </Box>
            {/* Comments Section*/}
            <div
                className="form-comment-section"
                style={{
                    isolation: 'isolate',
                    position: 'relative',
                    zIndex: 1,
                    marginTop: '35px'
                }}
            >
                {role !== 'customer' && <CommentsList
                    prevComments={comments}
                    pagination={{}}
                    filters={{}}
                    updateFilters={() => { }}
                    persona={role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role) ? "customer" : "admin"}
                    showControls={false}
                    showFilters={false}
                    showSorting={false}
                    showPageSize={false}
                    showResultsInfo={false}
                    showPagination={false}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                />}
                {role && rolesValidated.some(
                    (r) => r.role === role && r.status === requestProgressStatus
                ) && (
                        <CommentToggle
                            onAddComment={handleAddComment}
                            persona={(role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "customer" : "admin"}
                            buttonText="Comment"
                            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
                            hasComments={comments && comments.length > 0}
                        />
                    )
                }
            </div>
        </div>
    );
}

export default Ectd;